
import { Injectable, signal, inject, computed } from '@angular/core';
import { SupabaseService } from './supabase.service';
import { AuthError, User, Session } from '@supabase/supabase-js';
import { Router } from '@angular/router';
import { Profile } from '../models/data.models';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    public supabase = inject(SupabaseService);
    private router = inject(Router);

    // Signals for state management. The user object is replaced on every auth event (token
    // refresh, tab focus…): it only counts as a change when the account or its data changed, so
    // that effects depending on it (e.g. My profile, which fills its form) do not re-run.
    currentUser = signal<User | null>(null, {
        equal: (a, b) => a?.id === b?.id && a?.updated_at === b?.updated_at,
    });
    currentSession = signal<Session | null>(null);
    userProfile = signal<Profile | null>(null);
    isInitialized = signal(false);
    /** True once the user arrived through a password-reset link, until the password is changed. */
    passwordRecovery = signal(false);

    // Computed signals
    isAuthenticated = computed(() => !!this.currentUser());
    isAdmin = computed(() => !!this.userProfile()?.isAdmin);

    constructor() {
        // Initialize session — set isInitialized only after profile is fetched. Guards wait for
        // it: it must be set even when restoring the session fails.
        this.supabase.client.auth.getSession()
            .then(({ data }) => this.updateState(data.session))
            .catch(error => console.error('Error restoring the session:', error))
            .finally(() => this.isInitialized.set(true));

        // Listen to changes
        this.supabase.client.auth.onAuthStateChange((event, session) => {
            if (event === 'PASSWORD_RECOVERY') {
                // The reset link may land on another page (Supabase falls back to its Site URL
                // when the redirect is not allowed): always finish on the new-password form.
                this.passwordRecovery.set(true);
                if (!this.router.url.startsWith('/reset-password')) {
                    void this.router.navigate(['/reset-password']);
                }
            }
            if (event === 'TOKEN_REFRESHED') {
                // Same user and profile: only the tokens changed.
                this.currentSession.set(session);
                return;
            }
            void this.updateState(session);

            if (event === 'SIGNED_OUT') {
                this.userProfile.set(null);
                this.router.navigate(['/login']);
            }
        });
    }

    private async updateState(session: Session | null) {
        this.currentSession.set(session);
        this.currentUser.set(session?.user ?? null);

        if (session?.user) {
            await this.fetchProfile(session.user.id);
        } else {
            this.userProfile.set(null);
        }
    }

    async fetchProfile(userId: string) {
        const { data, error } = await this.supabase.client
            .from('profiles')
            .select('*')
            .eq('id', userId)
            .single();

        if (error) {
            console.error('Error fetching profile:', error);
        } else {
            const mappedProfile: Profile = {
                id: data.id,
                username: data.username,
                fullName: data.full_name,
                firstName: data.first_name,
                lastName: data.last_name,
                avatarUrl: data.avatar_url,
                dob: data.dob,
                isAdmin: data.is_admin,
                updatedAt: data.updated_at
            };
            this.userProfile.set(mappedProfile);
        }
    }

    async signInWithPassword(email: string, password: string) {
        return this.supabase.client.auth.signInWithPassword({
            email,
            password
        });
    }

    /**
     * Creates an account. The confirmation email links back to this site (the one the user is
     * on), not to the Supabase "Site URL", so dev and production each get their own link.
     */
    async signUp(email: string, password: string, metadata: Record<string, string>) {
        return this.supabase.client.auth.signUp({
            email,
            password,
            options: { data: metadata, emailRedirectTo: authRedirectUrl('/login') }
        });
    }

    async resendConfirmation(email: string) {
        return this.supabase.client.auth.resend({
            type: 'signup',
            email,
            options: { emailRedirectTo: authRedirectUrl('/login') }
        });
    }

    async requestPasswordReset(email: string) {
        return this.supabase.client.auth.resetPasswordForEmail(email, {
            redirectTo: authRedirectUrl('/reset-password')
        });
    }

    async updatePassword(password: string) {
        const result = await this.supabase.client.auth.updateUser({ password });
        if (!result.error) this.passwordRecovery.set(false);
        return result;
    }

    async signOut() {
        await this.supabase.client.auth.signOut();
    }
}

/** Absolute URL on the current site, used as the target of links sent by email. */
export function authRedirectUrl(path: string): string {
    return `${window.location.origin}${path}`;
}

/**
 * Error code Supabase appends to the URL when an email link fails (expired, already used…),
 * e.g. `#error=access_denied&error_code=otp_expired`. Supabase leaves it in place on failure.
 */
export function authLinkErrorFromUrl(): string | null {
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    return params.get('error_code') ?? params.get('error');
}

/** Translation key describing a Supabase auth error to the user. */
export function authErrorKey(error: AuthError | string | null | undefined): string {
    const code = typeof error === 'string' ? error : error?.code;
    switch (code) {
        case 'invalid_credentials':
            return 'auth.error.invalidCredentials';
        case 'email_not_confirmed':
            return 'auth.error.emailNotConfirmed';
        case 'user_already_exists':
        case 'email_exists':
            return 'auth.error.userAlreadyExists';
        case 'weak_password':
            return 'auth.error.weakPassword';
        case 'same_password':
            return 'auth.error.samePassword';
        case 'email_address_invalid':
            return 'auth.error.invalidEmail';
        case 'over_email_send_rate_limit':
        case 'over_request_rate_limit':
            return 'auth.error.rateLimit';
        case 'signup_disabled':
            return 'auth.error.signupDisabled';
        case 'otp_expired':
        case 'access_denied':
            return 'auth.error.linkExpired';
        default:
            return 'auth.error.unexpected';
    }
}
